// src/pages/Chat/ChatWindow.jsx
import React, { useState, useEffect} from 'react';
import { useTranslation } from 'react-i18next'; // 1. Import hooks
import { systemPrompts } from './chatPrompts';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import { getChatMessages, sendMessageStream, deleteChat as apiDeleteChat, updateMessage as apiUpdateMessage } from '../../services/chatApi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';


const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €"; 

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};



const ChatWindow = ({ chatId, onDeleteChat, onTitleUpdate, onCreateChat }) => {
    const navigate = useNavigate();
    const { user, setCredits } = useAuth();
    const { t, i18n } = useTranslation();
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isStreaming, setIsStreaming] = useState(false);

    const [inputContent, setInputContent] = useState('');


    const setInputContentHandler = (content) => {
        setInputContent(content);
    };

    // Function to handle updating a message and deleting subsequent ones
    const handleUpdateMessage = async (chatId, messageIndex, newContentForInput) => {
        try {
            await apiUpdateMessage(chatId, messageIndex, newContentForInput);
            // Update frontend state: keep messages up to and including the edited one
            setMessages(prevMessages => prevMessages.slice(0, messageIndex));
        } catch (err) {
            console.error('Failed to update message and delete subsequent ones:', err);
            setError('Failed to update message.');
            // Optionally re-fetch messages to ensure frontend state matches backend if the API call failed silently after deletion
        }
    };

    useEffect(() => {
        const fetchMessages = async () => {
            if (!chatId) {
                 // If no chatId is provided, clear messages and set loading to false
                 setMessages([]);
                 setIsLoading(false);
                 setError(null);
                 return;
            }
            setIsLoading(true);
            try {
                const fetchedMessages = await getChatMessages(chatId);
                setMessages(fetchedMessages.filter(msg => msg.role !== 'system'));
                setError(null);
            } catch (err) {
                setError('Failed to load messages.');
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchMessages();
    }, [chatId]);

    const handleSendMessage = async (content, webSearch) => {
        console.log(user)
        console.log(`Sending message with Web Search: ${webSearch}`);
        const userId = user?.user_id;
        if (!userId) {
            console.error("User ID not available. Cannot send message.");
            setError("Authentication error. Please log in again.");
            return;
        }
        setIsStreaming(true);
        // Add user message immediately for better UX
        const userMessage = { role: 'user', content, created_at: new Date().toISOString() };
        // Add a placeholder for the assistant's response
        const assistantPlaceholder = { role: 'assistant', content: '', citations: [], created_at: new Date().toISOString() };
        setMessages(prev => [...prev, userMessage, assistantPlaceholder]);

        let systemPrompt = null;
        if (messages.length === 0) {
            const currentLang = i18n.language.split('-')[0];
            systemPrompt = systemPrompts[currentLang] || systemPrompts['en'];
            console.log(`First message in chat. Attaching system prompt for language: ${currentLang}`);
        }
        
        await sendMessageStream({
            chatId,
            content,
            user_id: userId,
            web_search: webSearch, // You can add a UI element for this
            systemPrompt,
            onChunk: (chunk) => {
                setMessages(prev => {
                    const lastMessage = prev[prev.length - 1];
                    if (lastMessage.role === 'assistant') {
                        return [
                            ...prev.slice(0, -1),
                            { ...lastMessage, content: lastMessage.content + chunk },
                        ];
                    }
                    return prev;
                });
            },
            onCitations: (citations) => {
                console.log("Received citations:", citations);
                setMessages(prev => {
                    const newMessages = [...prev]; // Create a new array
                    const lastMessage = prev[prev.length - 1];
                    if (lastMessage.role === 'assistant') {
                        const updatedCitations = Array.isArray(citations) ? citations : lastMessage.citations || [];
                        newMessages[newMessages.length - 1] = { ...lastMessage, citations: updatedCitations };
                        }
                    return newMessages;
                });
            },

            onDone: (data) => {
                console.log('Stream done:', data);
                setIsStreaming(false);
                // Optionally update the message with the final ID from the server
                if (data && data.new_title ) {
                    // Call the function passed from the parent!
                    console.log(`Title updated for chat ${chatId}. New title: ${data.new_title}`);
                    onTitleUpdate(chatId, data.new_title);
                }
                if (data && data.new_credits !== undefined) {
                    console.log(`Calling setCredits with: ${data.new_credits}`); // <-- ADD THIS LOG
                    setCredits(data.new_credits);
                }
                setInputContent('');
            },
            onError: (errorMessage) => {
                setMessages(prev => {
                    const lastMessage = prev[prev.length - 1];
                    if (lastMessage.role === 'assistant') {
                        return [
                            ...prev.slice(0, -1),
                            { ...lastMessage, content: `Error: ${errorMessage}` },
                        ];
                    }
                    return prev;
                });
                setIsStreaming(false);
            }
        });
    };

    const handleCreateNewChat = () => {
        if (onCreateChat) {
            onCreateChat(); // Call the function passed from the parent (e.g., ChatPage)
        } else {
            console.error("onCreateChat function not provided to ChatWindow.");
            // Fallback: Navigate to a general chat route, but this might not create a new chat instance properly
            console.log("Navigating to /chat as a fallback.");
            navigate('/chat');
        }
    };


    return (
    <div className="flex-1 flex flex-col bg-brand-white overflow-hidden">
        
        {/* LOGIC BLOCK 1: Check Subscription Status */}
        {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
            <div className="p-6 md:p-8 h-full overflow-y-auto">
                <SubscribePrompt />
            </div>
            
        /* LOGIC BLOCK 2: Check Credits */
        ) : user?.credits <= 0 ? (
            <div className="p-6 md:p-8 h-full overflow-y-auto">
                <PurchaseCreditsPrompt />
            </div>

        /* LOGIC BLOCK 3: Access Granted - Render Chat Interface */
        ) : (
            <>
                {messages.length === 0 && !chatId && (
                    <div className="flex-shrink-0 px-6 py-8 border-b border-gray-200">
                        <h1 className="text-3xl font-bold text-brand-orange mb-4">
                            {t('chat.title')}
                        </h1>
                        <p className="text-gray-600 text-base leading-relaxed max-w-3xl">
                            {t('chat.introduction')}
                        </p>
                        <button
                            onClick={handleCreateNewChat}
                            className="px-4 py-2 bg-brand-orange hover:bg-orange-600 text-white font-semibold rounded-md transition-colors"
                        >
                            {t('chat.sidebar.newChatButton')}
                        </button>
                    </div>
                )}

                {/* This container will hold the list */}
                <MessageList 
                    messages={messages} 
                    isLoading={isLoading} 
                    error={error} 
                    onUpdateMessage={handleUpdateMessage} 
                    chatId={chatId} 
                    onSendMessage={handleSendMessage} 
                    setInputContent={setInputContentHandler} 
                />

                {(messages.length > 0 || chatId) && (
                    <div className="flex-shrink-0">
                        <MessageInput 
                            onSendMessage={handleSendMessage} 
                            isStreaming={isStreaming} 
                            initialContent={inputContent} 
                        />
                    </div>
                )}
            </>
        )}
    </div>
);
};
export default ChatWindow;
